// src/Hooks/useDrawerLogic.js

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { DrawerService } from '../Components/Drawer/CustomDrawer/DrawerService';
import {
  getInitialPhotos,
  getInitialPhotoDescs,
  getInitialPhotoSelection,
  getInitialDocs,
} from '../Components/Drawer/CustomDrawer/DrawerUtils';
import { InfoTabControls } from '../Components/Drawer/InfoTab/InfoTabControls';
import { useIcons } from '../Contexts/IconContext';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../Firebase/firebase';
import { v4 as uuidv4 } from 'uuid';

let cachedActiveTab = 'MARKERS';

export function useDrawerLogic({
  activeMenu,
  activeTool,
  setActiveTool,
  onAction,
  drawingStyles,
  setDrawingStyles,
  selectedMarker,
  setIsDrawerEditing,
  onDeselect,
  closeTrigger,
}) {
  const [activeTab, setActiveTabState] = useState(cachedActiveTab);

  const setActiveTab = useCallback((tab) => {
    cachedActiveTab = tab;
    setActiveTabState(tab);
  }, []);

  const [localActiveAction, setLocalActiveAction] = useState(() => {
    if (activeMenu === 'MARKUP_MARKERS' && selectedMarker) return 'EDIT';
    return 'None';
  });

  const localActiveActionRef = useRef(localActiveAction);
  const [pointsPlaced, setPointsPlaced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(isSaving);

  const prevCloseTriggerRef = useRef(closeTrigger);
  const closeTimeoutRef = useRef(null);

  const [tempData, setTempData] = useState(() =>
    selectedMarker ? JSON.parse(JSON.stringify(selectedMarker)) : {},
  );
  const tempDataRef = useRef(tempData);

  const linkingGroupIdRef = useRef(null);
  const lastFetchedIdRef = useRef(null);

  const draftsCacheRef = useRef({});
  const draftSiblingsRef = useRef({ marker: null, polygon: null, line: null });
  const pristineSnapshotRef = useRef(null);
  const createdInSessionRef = useRef(new Set()); // AMENDED: Tracks creations during uninterrupted session

  const [isInfoEditing, setIsInfoEditing] = useState(() => {
    return !!(activeMenu && activeMenu.startsWith('MARKUP') && selectedMarker);
  });

  const isTransitioningRef = useRef(false);
  const isInternalUpdateRef = useRef(false);
  const isDeletingRef = useRef(false);

  const justSavedRef = useRef(false);

  // AMENDED: Destructure markerIcons from useIcons context
  const { markerIcons, defaultIcon, defaultIconID } = useIcons();

  // DIRTY STATES
  const [markerDirty, setMarkerDirty] = useState(false);
  const markerDirtyRef = useRef(false);

  const [infoDirty, setInfoDirty] = useState(false);
  const infoDirtyRef = useRef(false);

  const [photosDirty, setPhotosDirty] = useState(false);
  const photosDirtyRef = useRef(false);

  const [docsDirty, setDocsDirty] = useState(false);
  const docsDirtyRef = useRef(false);

  // AMENDED: State for Global Default RTE Fonts
  const [rteFontType, setRteFontType] = useState('Arial');
  const [rteFontSize, setRteFontSize] = useState('11pt');

  // AMENDED: Fetch the RTEDefaultText settings from Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'RTEDefaultText'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.FontType) setRteFontType(data.FontType);
        if (data.FontSize) setRteFontSize(data.FontSize);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    infoDirtyRef.current = infoDirty;
  }, [infoDirty]);
  useEffect(() => {
    photosDirtyRef.current = photosDirty;
  }, [photosDirty]);
  useEffect(() => {
    docsDirtyRef.current = docsDirty;
  }, [docsDirty]);

  const lastSyncedEditIdRef = useRef(null);

  useEffect(() => {
    if (localActiveAction === 'EDIT' && selectedMarker && !selectedMarker.isNew) {
      if (lastSyncedEditIdRef.current !== selectedMarker.id) {
        lastSyncedEditIdRef.current = selectedMarker.id;
        const timer = setTimeout(() => {
          onAction('EDIT');
        }, 50);
        return () => clearTimeout(timer);
      }
    } else if (!selectedMarker || localActiveAction !== 'EDIT') {
      lastSyncedEditIdRef.current = null;
    }
  }, [localActiveAction, selectedMarker, onAction]);

  const updatePhotoItems = useCallback((items, makeDirty = true) => {
    setPhotoItems(items);
    photoItemsRef.current = items;
    if (makeDirty) {
      photosDirtyRef.current = true;
      setPhotosDirty(true);
    }
  }, []);

  const updateDocItems = useCallback((items, makeDirty = true) => {
    setDocItems(items);
    docItemsRef.current = items;
    if (makeDirty) {
      docsDirtyRef.current = true;
      setDocsDirty(true);
    }
  }, []);

  const [photoItems, setPhotoItems] = useState(() => getInitialPhotos(selectedMarker));
  const [photoDescriptions, setPhotoDescriptions] = useState(() =>
    getInitialPhotoDescs(selectedMarker),
  );
  const [docItems, setDocItems] = useState(() => getInitialDocs(selectedMarker));

  const photoItemsRef = useRef(photoItems);
  const photoDescriptionsRef = useRef(photoDescriptions);
  const docItemsRef = useRef(docItems);

  useEffect(() => {
    photoItemsRef.current = photoItems;
  }, [photoItems]);
  useEffect(() => {
    photoDescriptionsRef.current = photoDescriptions;
  }, [photoDescriptions]);
  useEffect(() => {
    docItemsRef.current = docItems;
  }, [docItems]);

  const [photoSelectedIds, setPhotoSelectedIds] = useState(() =>
    getInitialPhotoSelection(selectedMarker),
  );
  const [photoLastSelectedId, setPhotoLastSelectedId] = useState(() => {
    const photos = selectedMarker?.photos;
    return photos && photos.length > 0 ? photos[0].id : null;
  });
  const [docSelectedIds, setDocSelectedIds] = useState(new Set());

  const [photosTrigger, setPhotosTrigger] = useState(0);
  const [photosDeleteTrigger, setPhotosDeleteTrigger] = useState(0);
  const [docsTrigger, setDocsTrigger] = useState(0);
  const [docsDeleteTrigger, setDocsDeleteTrigger] = useState(0);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [unsavedMessage, setUnsavedMessage] = useState('');
  const [alert, setAlert] = useState({ visible: false, severity: '', message: '' });

  const isDirtyGlobal = markerDirty || infoDirty || photosDirty || docsDirty;

  const fileInputRef = useRef(null);

  const setAction = useCallback((action) => {
    setLocalActiveAction(action);
    localActiveActionRef.current = action;
  }, []);

  useEffect(() => {
    if (['INFO', 'VIS_HOVER', 'PHOTOS', 'DOCS'].includes(activeTab)) {
      if (selectedMarker && localActiveActionRef.current === 'None') {
        setAction('EDIT');
        setPointsPlaced(true);
      }
    }
  }, [activeTab, selectedMarker, setAction]);

  const forceClean = useCallback(() => {
    markerDirtyRef.current = false;
    setMarkerDirty(false);
    infoDirtyRef.current = false;
    setInfoDirty(false);
    photosDirtyRef.current = false;
    setPhotosDirty(false);
    docsDirtyRef.current = false;
    setDocsDirty(false);
    draftsCacheRef.current = {};
    draftSiblingsRef.current = { marker: null, polygon: null, line: null };
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const resetTabsState = useCallback(() => {
    updatePhotoItems([], false);
    setPhotoDescriptions({});
    setPhotoSelectedIds(new Set());
    setPhotoLastSelectedId(null);
    updateDocItems([], false);
    setDocSelectedIds(new Set());
  }, [updatePhotoItems, updateDocItems]);

  const hydrateTabsState = useCallback(
    (marker) => {
      if (!marker || marker.isNew) {
        resetTabsState();
        return;
      }
      const initialPhotos = getInitialPhotos(marker);
      updatePhotoItems(initialPhotos, false);
      setPhotoDescriptions(getInitialPhotoDescs(marker));

      const initialPhotoSelection = getInitialPhotoSelection(marker);
      setPhotoSelectedIds(initialPhotoSelection);
      setPhotoLastSelectedId(initialPhotos.length > 0 ? initialPhotos[0].id : null);

      updateDocItems(getInitialDocs(marker), false);
      setDocSelectedIds(new Set());
    },
    [updatePhotoItems, updateDocItems, resetTabsState],
  );

  const showAlert = useCallback((severity, message) => {
    setAlert({ visible: true, severity, message });
    setTimeout(() => {
      setAlert({ visible: false, severity: '', message: '' });
    }, 2000);
  }, []);

  const handleDataUpdate = useCallback((update) => {
    setTempData((prev) => {
      const next = typeof update === 'function' ? update(prev) : { ...prev, ...update };
      tempDataRef.current = next; // Instant sync
      return next;
    });
    infoDirtyRef.current = true;
    setInfoDirty(true);
  }, []);

  const infoControls = useMemo(() => {
    return new InfoTabControls(setIsInfoEditing, handleDataUpdate, selectedMarker);
  }, [setIsInfoEditing, handleDataUpdate, selectedMarker]);

  const handleMarkerFieldChange = useCallback((key, value) => {
    setTempData((prev) => {
      const next = { ...prev, [key]: value };
      tempDataRef.current = next; // Instant sync
      return next;
    });
    markerDirtyRef.current = true;
    setMarkerDirty(true);
  }, []);

  const handleInfoTabChange = useCallback(
    (newDataFunc) => handleDataUpdate(newDataFunc),
    [handleDataUpdate],
  );

  const checkTypeExistsInGroup = useCallback(
    (groupId, toolType) => {
      if (!groupId) return false;
      const strId = String(groupId);

      const currentDraftGroupId = String(
        tempDataRef.current?.markerGroupID ||
          selectedMarker?.markerGroupID ||
          selectedMarker?.id,
      );

      if (currentDraftGroupId === strId) {
        const t = (selectedMarker?.type || tempDataRef.current?.type || '').toLowerCase();
        if (toolType === 'MARKER' && t === 'marker') return true;
        if (toolType === 'POLYGON' && t === 'polygon') return true;
        if (toolType === 'LINE' && (t === 'line' || t === 'polyline')) return true;
      }

      if (toolType === 'MARKER') {
        const arr = window.__markerArray || [];
        return arr.some((item) => String(item.markerGroupID || item.id) === strId);
      }
      if (toolType === 'POLYGON') {
        const arr = window.__polygonArray || [];
        return arr.some((item) => String(item.markerGroupID || item.id) === strId);
      }
      if (toolType === 'LINE') {
        const arr = window.__polylineArray || [];
        return arr.some((item) => String(item.markerGroupID || item.id) === strId);
      }
      return false;
    },
    [selectedMarker],
  );

  const handleToolClick = useCallback(
    (newTool) => {
      isTransitioningRef.current = true;
      justSavedRef.current = false;

      setPointsPlaced(false);
      if (setActiveTool) setActiveTool(newTool);
      setIsInfoEditing(true);

      let proposedGroupId =
        tempDataRef.current?.markerGroupID ||
        linkingGroupIdRef.current ||
        selectedMarker?.markerGroupID ||
        selectedMarker?.id;

      if (proposedGroupId && checkTypeExistsInGroup(proposedGroupId, newTool)) {
        proposedGroupId = null;
      }

      const groupId = proposedGroupId || uuidv4();
      linkingGroupIdRef.current = groupId;
      createdInSessionRef.current.add(groupId); // AMENDED: Track new creation in session

      // AMENDED: Fetch default icon to check for category assignment
      const defIconObj = markerIcons?.find((i) => String(i.id) === String(defaultIconID));

      setTempData((prev) => {
        const next = { ...prev, markerGroupID: groupId };
        if (newTool === 'MARKER') {
          next.categoryId = defIconObj?.categoryId || '';
        }
        tempDataRef.current = next;
        return next;
      });

      if (newTool === 'MARKER') {
        setDrawingStyles((prev) => ({
          ...prev,
          iconID: defaultIconID,
          markerIcon: defaultIcon ? defaultIcon.split('/').pop() : null,
        }));
      } else if (newTool === 'POLYGON') {
        setDrawingStyles((prev) => ({
          ...prev,
          polygonColor: '#FF9900',
        }));
      } else if (newTool === 'LINE') {
        setDrawingStyles((prev) => ({
          ...prev,
          lineColor: '#00B0FF',
          lineWeight: 4,
          isDashed: false,
          dashColor: '#000000',
          dashLength: 10,
        }));
      }

      onAction('ADD_NEW', { preserveDrafts: true, markerGroupID: groupId });
      setAction('ADD_NEW');
    },
    [
      setActiveTool,
      setAction,
      onAction,
      defaultIcon,
      defaultIconID,
      setDrawingStyles,
      selectedMarker,
      checkTypeExistsInGroup,
      markerIcons,
    ],
  );

  const handleTabSwitch = useCallback(
    (newTab, newTool) => {
      if (newTab === activeTab) return;

      const capturedGroupId =
        tempDataRef.current?.markerGroupID ||
        linkingGroupIdRef.current ||
        selectedMarker?.markerGroupID ||
        selectedMarker?.id;

      if (capturedGroupId) {
        let targetType = null;
        let exists = false;
        let targetId = null;
        let targetDataFromArrays = null;

        const markers = window.__markerArray || [];
        const polygons = window.__polygonArray || [];
        const polylines = window.__polylineArray || [];

        if (newTab === 'MARKERS') {
          targetType = 'marker';
          targetDataFromArrays = markers.find(
            (m) =>
              (m.markerGroupID && String(m.markerGroupID) === String(capturedGroupId)) ||
              String(m.id) === String(capturedGroupId),
          );
          targetId =
            targetDataFromArrays?.id ||
            draftSiblingsRef.current.marker ||
            selectedMarker?.markerID;
          exists = !!targetId;
        } else if (newTab === 'POLYGON') {
          targetType = 'polygon';
          targetDataFromArrays = polygons.find(
            (p) =>
              (p.markerGroupID && String(p.markerGroupID) === String(capturedGroupId)) ||
              String(p.id) === String(capturedGroupId),
          );
          targetId =
            targetDataFromArrays?.id ||
            draftSiblingsRef.current.polygon ||
            selectedMarker?.polygonID;
          exists = !!targetId;
        } else if (newTab === 'POLYLINE') {
          targetType = 'line';
          targetDataFromArrays = polylines.find(
            (l) =>
              (l.markerGroupID && String(l.markerGroupID) === String(capturedGroupId)) ||
              String(l.id) === String(capturedGroupId),
          );
          targetId =
            targetDataFromArrays?.id ||
            draftSiblingsRef.current.line ||
            selectedMarker?.polylineID;
          exists = !!targetId;
        }

        setActiveTab(newTab);
        if (setActiveTool) setActiveTool(newTool);

        if (exists && targetId) {
          const cachedData = draftsCacheRef.current[targetId] || {};
          const fullTargetData = targetDataFromArrays || cachedData;

          if (targetType === 'line' && setDrawingStyles) {
            setDrawingStyles((prev) => ({
              ...prev,
              lineColor: fullTargetData.color || '#00B0FF',
              lineWeight: fullTargetData.weight || 4,
              isDashed: fullTargetData.isDashed || false,
              dashColor: fullTargetData.dashColor || '#000000',
              dashLength: fullTargetData.dashLength || 10,
            }));
          }

          setAction('EDIT');
          setPointsPlaced(true);

          if (selectedMarker?.id !== targetId) {
            onAction('FORCE_SELECT', {
              ...fullTargetData,
              id: targetId,
              type: targetType,
              markerGroupID: capturedGroupId,
            });
          }
        } else {
          setAction('None');
          setPointsPlaced(false);
          linkingGroupIdRef.current = capturedGroupId;
        }
        return;
      }

      setActiveTab(newTab);
      if (setActiveTool && ['MARKERS', 'POLYGON', 'POLYLINE'].includes(newTab)) {
        setActiveTool(newTool);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeTab,
      selectedMarker,
      onAction,
      setAction,
      setActiveTab,
      setActiveTool,
      setDrawingStyles,
    ],
  );

  const handleStyleChange = useCallback(
    (newStyles) => {
      const current = drawingStyles;
      let hasChanged = false;

      for (const key in newStyles) {
        const newVal = newStyles[key];
        const oldVal = current[key];

        if (newVal !== oldVal) {
          if (typeof newVal === 'string' && typeof oldVal === 'string') {
            if (newVal.toLowerCase() !== oldVal.toLowerCase()) {
              hasChanged = true;
              break;
            }
          } else {
            hasChanged = true;
            break;
          }
        }
      }

      if (!hasChanged) return;

      setDrawingStyles((prev) => ({ ...prev, ...newStyles }));

      setTempData((prev) => {
        const updates = {};
        if (newStyles.markerIcon !== undefined) updates.icon = newStyles.markerIcon;
        if (newStyles.iconID !== undefined) {
          updates.iconID = newStyles.iconID;

          // AMENDED: Auto-assign category for NEW markers allowing dynamic swapping
          const currentGroupId =
            prev.markerGroupID ||
            selectedMarker?.markerGroupID ||
            linkingGroupIdRef.current;
          const isSessionCreated =
            currentGroupId && createdInSessionRef.current.has(currentGroupId);
          const isNewCreation =
            !selectedMarker ||
            selectedMarker.isNew ||
            localActiveActionRef.current === 'ADD_NEW' ||
            isSessionCreated;

          if (isNewCreation) {
            const selectedIconObj = markerIcons?.find(
              (i) => String(i.id) === String(newStyles.iconID),
            );
            if (selectedIconObj) {
              updates.categoryId = selectedIconObj.categoryId || '';
            }
          }
        }
        if (newStyles.polygonColor !== undefined) updates.color = newStyles.polygonColor;
        if (newStyles.lineColor !== undefined) updates.color = newStyles.lineColor;
        if (newStyles.lineWeight !== undefined) updates.weight = newStyles.lineWeight;

        if (newStyles.isDashed !== undefined) updates.isDashed = newStyles.isDashed;
        if (newStyles.dashColor !== undefined) updates.dashColor = newStyles.dashColor;
        if (newStyles.dashLength !== undefined) updates.dashLength = newStyles.dashLength;

        const targetId = prev.id || selectedMarker?.id;
        if (targetId && draftsCacheRef.current[targetId]) {
          draftsCacheRef.current[targetId] = {
            ...draftsCacheRef.current[targetId],
            ...updates,
          };
        }

        const next = { ...prev, ...updates };
        tempDataRef.current = next; // Instant sync
        return next;
      });

      markerDirtyRef.current = true;
      setMarkerDirty(true);
    },
    [drawingStyles, setDrawingStyles, selectedMarker, markerIcons],
  );

  const syncGroupStylesToUI = useCallback(
    (marker) => {
      if (!setDrawingStyles || !marker) return;

      const safeString = (val, defaultVal = '') => {
        if (typeof val === 'string') return val;
        if (typeof val === 'number') return String(val);
        return defaultVal;
      };

      const t = (marker.type || '').toLowerCase();
      const groupId = marker.markerGroupID || marker.id;

      const polygons = window.__polygonArray || [];
      const polylines = window.__polylineArray || [];
      const markers = window.__markerArray || [];

      const targetPolygon =
        t === 'polygon'
          ? marker
          : polygons.find(
              (p) => (groupId && p.markerGroupID === groupId) || p.id === groupId,
            );
      const targetLine =
        t === 'line' || t === 'polyline'
          ? marker
          : polylines.find(
              (l) => (groupId && l.markerGroupID === groupId) || l.id === groupId,
            );
      const targetMarker =
        t === 'marker'
          ? marker
          : markers.find(
              (m) => (groupId && m.markerGroupID === groupId) || m.id === groupId,
            );

      setDrawingStyles((prevStyles) => {
        const updates = { ...prevStyles };
        let hasChanges = false;

        if (targetPolygon) {
          const pColor = safeString(targetPolygon.color, '#FF9900');
          if (safeString(updates.polygonColor).toLowerCase() !== pColor.toLowerCase()) {
            updates.polygonColor = pColor;
            hasChanges = true;
          }
        }

        if (targetLine) {
          const lColor = safeString(targetLine.color, '#00B0FF');
          if (safeString(updates.lineColor).toLowerCase() !== lColor.toLowerCase()) {
            updates.lineColor = lColor;
            hasChanges = true;
          }

          const lWeight = targetLine.weight !== undefined ? targetLine.weight : 4;
          if (updates.lineWeight !== lWeight) {
            updates.lineWeight = lWeight;
            hasChanges = true;
          }

          const lDashed = targetLine.isDashed || false;
          if (updates.isDashed !== lDashed) {
            updates.isDashed = lDashed;
            hasChanges = true;
          }

          const lDashColor = safeString(targetLine.dashColor, '#000000');
          if (safeString(updates.dashColor).toLowerCase() !== lDashColor.toLowerCase()) {
            updates.dashColor = lDashColor;
            hasChanges = true;
          }

          const lDashLength = targetLine.dashLength || 10;
          if (updates.dashLength !== lDashLength) {
            updates.dashLength = lDashLength;
            hasChanges = true;
          }
        }

        if (targetMarker) {
          let markerChanged = false;
          if (
            targetMarker.icon !== undefined &&
            updates.markerIcon !== targetMarker.icon
          ) {
            updates.markerIcon = targetMarker.icon;
            markerChanged = true;
          }
          if (
            targetMarker.iconID !== undefined &&
            updates.iconID !== targetMarker.iconID
          ) {
            updates.iconID = targetMarker.iconID;
            markerChanged = true;
          }

          if (markerChanged) {
            hasChanges = true;
            if (targetMarker.icon === undefined) updates.markerIcon = null;
            if (targetMarker.iconID === undefined) updates.iconID = null;
          }
        }

        return hasChanges ? updates : prevStyles;
      });
    },
    [setDrawingStyles],
  );

  const executeClose = useCallback(() => {
    isTransitioningRef.current = false;

    const effectivelyNew =
      selectedMarker && selectedMarker.isNew && !justSavedRef.current;
    const isNewCreation =
      (localActiveActionRef.current === 'ADD_NEW' && !justSavedRef.current) ||
      effectivelyNew;

    justSavedRef.current = false;

    if (
      localActiveActionRef.current === 'EDIT' ||
      localActiveActionRef.current === 'ADD_NEW'
    ) {
      if (isNewCreation) {
        isDeletingRef.current = true;
        onAction('DELETE');
        setTimeout(() => {
          isDeletingRef.current = false;
        }, 800);
      } else {
        onAction('CANCEL');
      }
      if (onDeselect) {
        closeTimeoutRef.current = setTimeout(() => onDeselect(), 200);
      }
    } else {
      onAction('CLOSE');
      if (onDeselect) onDeselect();
    }
    setAction('None');
    setPointsPlaced(false);
    forceClean();
    resetTabsState();
    setTempData({});
    linkingGroupIdRef.current = null;
    createdInSessionRef.current = new Set();
    if (activeTab === 'INFO') infoControls.handleCancel();

    setActiveTab('MARKERS');
    if (setActiveTool) setActiveTool('MARKER');
  }, [
    onAction,
    onDeselect,
    activeTab,
    infoControls,
    selectedMarker,
    setAction,
    forceClean,
    resetTabsState,
    setActiveTab,
    setActiveTool,
  ]);

  const handleConfirmClose = useCallback(() => {
    setShowUnsavedAlert(false);

    const effectivelyNew =
      selectedMarker && selectedMarker.isNew && !justSavedRef.current;
    const isUnsavedNew =
      (localActiveActionRef.current === 'ADD_NEW' && !justSavedRef.current) ||
      effectivelyNew;

    if (isUnsavedNew) {
      executeClose();
    } else {
      forceClean();

      if (selectedMarker && selectedMarker.id) {
        onAction('CANCEL');

        const rollbackData = pristineSnapshotRef.current || selectedMarker;

        isTransitioningRef.current = true;

        if (onDeselect) onDeselect();

        setTimeout(() => {
          onAction('FORCE_SELECT', rollbackData);

          setTimeout(() => {
            onAction('EDIT');
            isTransitioningRef.current = false;
          }, 100);
        }, 100);

        const raw = JSON.parse(JSON.stringify(rollbackData));
        const typeStr = (raw.type || '').toLowerCase();

        raw.markerID = raw.markerID || rollbackData.markerID || null;
        raw.polygonID = raw.polygonID || rollbackData.polygonID || null;
        raw.polylineID = raw.polylineID || rollbackData.polylineID || null;

        if (typeStr === 'marker') {
          raw.groupHasMarker = true;
          raw.markerID = raw.id;
        } else if (typeStr === 'polygon') {
          raw.groupHasPolygon = true;
          raw.polygonID = raw.id;
        } else if (typeStr === 'line' || typeStr === 'polyline') {
          raw.groupHasPolyline = true;
          raw.polylineID = raw.id;
        }

        setTempData(raw);
        syncGroupStylesToUI(rollbackData);
        lastFetchedIdRef.current = null;
      }
    }
  }, [
    executeClose,
    forceClean,
    selectedMarker,
    onAction,
    syncGroupStylesToUI,
    onDeselect,
  ]);

  const handleCancelClose = useCallback(() => {
    setShowUnsavedAlert(false);
    onAction('ABORT');
  }, [onAction]);

  const handleConfirmDelete = useCallback(() => {
    isDeletingRef.current = true;
    isTransitioningRef.current = false;
    justSavedRef.current = false;

    let typeToDelete = (
      selectedMarker?.type ||
      tempDataRef.current?.type ||
      ''
    ).toLowerCase();
    if (activeTab === 'POLYGON') typeToDelete = 'polygon';
    else if (activeTab === 'POLYLINE') typeToDelete = 'line';
    else if (activeTab === 'MARKERS') typeToDelete = 'marker';

    let idToDelete = selectedMarker?.id || tempDataRef.current?.id || null;
    if (typeToDelete === 'polygon') {
      idToDelete =
        selectedMarker?.type?.toLowerCase() === 'polygon'
          ? selectedMarker.id
          : selectedMarker?.polygonID ||
            tempDataRef.current?.polygonID ||
            draftSiblingsRef.current.polygon ||
            idToDelete;
    } else if (typeToDelete === 'line' || typeToDelete === 'polyline') {
      idToDelete =
        selectedMarker?.type?.toLowerCase() === 'line' ||
        selectedMarker?.type?.toLowerCase() === 'polyline'
          ? selectedMarker.id
          : selectedMarker?.polylineID ||
            tempDataRef.current?.polylineID ||
            draftSiblingsRef.current.line ||
            idToDelete;
    } else if (typeToDelete === 'marker') {
      idToDelete =
        selectedMarker?.type?.toLowerCase() === 'marker'
          ? selectedMarker.id
          : selectedMarker?.markerID ||
            tempDataRef.current?.markerID ||
            draftSiblingsRef.current.marker ||
            idToDelete;
    }

    onAction('DELETE', { id: idToDelete, type: typeToDelete });
    setAction('None');
    setPointsPlaced(false);
    forceClean();
    resetTabsState();
    setTempData({});
    linkingGroupIdRef.current = null;
    createdInSessionRef.current = new Set();
    setShowDeleteConfirm(false);

    // Defer tab/tool reset so setActiveTool does not overwrite currentAction('DELETE') in the same render batch
    setTimeout(() => {
      isDeletingRef.current = false;
      setActiveTab('MARKERS');
      if (setActiveTool) setActiveTool('MARKER');
    }, 300);
  }, [
    selectedMarker,
    activeTab,
    onAction,
    setAction,
    forceClean,
    resetTabsState,
    setActiveTab,
    setActiveTool,
  ]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleHeaderAction = useCallback(
    async (actionName, options = {}) => {
      if (isDeletingRef.current) return;
      if (actionName === 'ADD_NEW') {
        let targetTool = 'MARKER';
        if (activeTab === 'POLYGON') targetTool = 'POLYGON';
        if (activeTab === 'POLYLINE') targetTool = 'LINE';

        if (setActiveTool) setActiveTool(targetTool);
        setPointsPlaced(false);

        let proposedGroupId =
          tempDataRef.current?.markerGroupID ||
          linkingGroupIdRef.current ||
          selectedMarker?.markerGroupID ||
          selectedMarker?.id;

        if (proposedGroupId && checkTypeExistsInGroup(proposedGroupId, targetTool)) {
          proposedGroupId = null;
        }

        const groupId = proposedGroupId || uuidv4();
        linkingGroupIdRef.current = groupId;
        createdInSessionRef.current.add(groupId); // AMENDED: Track new creation in session

        // AMENDED: Fetch default icon to check for category assignment
        const defIconObj = markerIcons?.find(
          (i) => String(i.id) === String(defaultIconID),
        );

        setTempData((prev) => {
          const next = { ...prev, markerGroupID: groupId };
          if (targetTool === 'MARKER') {
            next.categoryId = defIconObj?.categoryId || '';
          }
          tempDataRef.current = next;
          return next;
        });

        setIsInfoEditing(true);

        setDrawingStyles((prev) => {
          const updates = { ...prev };
          if (targetTool === 'MARKER') {
            updates.markerIcon = defaultIcon ? defaultIcon.split('/').pop() : null;
            updates.iconID = defaultIconID || null;
          } else if (targetTool === 'POLYGON') {
            updates.polygonColor = '#FF9900';
          } else if (targetTool === 'LINE') {
            updates.lineColor = '#00B0FF';
            updates.lineWeight = 4;
            updates.isDashed = false;
            updates.dashColor = '#000000';
            updates.dashLength = 10;
          }
          return updates;
        });

        onAction('ADD_NEW', { preserveDrafts: true, markerGroupID: groupId });
        setAction('ADD_NEW');
        return;
      }

      if (
        actionName === 'CLOSE' ||
        actionName === 'CANCEL' ||
        actionName === 'HEADER_CLOSE'
      ) {
        const effectivelyNew =
          selectedMarker && selectedMarker.isNew && !justSavedRef.current;
        const isUnsavedNew =
          (localActiveActionRef.current === 'ADD_NEW' &&
            pointsPlaced &&
            !justSavedRef.current) ||
          effectivelyNew;

        const isCurrentlyDirty =
          markerDirtyRef.current ||
          infoDirtyRef.current ||
          photosDirtyRef.current ||
          docsDirtyRef.current ||
          isUnsavedNew;

        if (isCurrentlyDirty) {
          setUnsavedMessage('Do you want to discard those changes?');
          setShowUnsavedAlert(true);
          return;
        }
        executeClose();
        return;
      }

      if (activeTab === 'PHOTOS') {
        if (actionName === 'ADD') return setPhotosTrigger(Date.now());
        if (actionName === 'DELETE') return setPhotosDeleteTrigger(Date.now());
      }
      if (activeTab === 'DOCS') {
        if (actionName === 'ADD') return setDocsTrigger(Date.now());
        if (actionName === 'DELETE') return setDocsDeleteTrigger(Date.now());
      }

      if (actionName === 'DELETE') {
        setShowDeleteConfirm(true);
        return;
      }
      if (actionName === 'EDIT') {
        if (selectedMarker?.id) {
          setIsInfoEditing(true);
          setPointsPlaced(true);
        }
      }

      if (actionName === 'SAVE') {
        setIsSaving(true);
        isSavingRef.current = true;

        try {
          const dataSnapshot = { ...tempDataRef.current };
          const markerSnapshot = { ...selectedMarker };

          const groupID =
            linkingGroupIdRef.current ||
            dataSnapshot.markerGroupID ||
            markerSnapshot.markerGroupID ||
            markerSnapshot.id ||
            uuidv4();

          const oldHoverImage = markerSnapshot.hoverImage;
          let finalHoverImage = dataSnapshot.hoverImage;

          if (dataSnapshot.file) {
            const uploadedUrl = await DrawerService.uploadMarkerImage(
              groupID,
              dataSnapshot.file,
            );
            if (uploadedUrl && typeof uploadedUrl === 'string') {
              finalHoverImage = uploadedUrl;
            }
          }

          const sanitize = (val) => (val === undefined ? null : val);
          finalHoverImage = sanitize(finalHoverImage);

          // AMENDED: Automatically clean up orphaned hover images upon saving
          if (
            oldHoverImage &&
            oldHoverImage.includes('firebasestorage') &&
            oldHoverImage !== finalHoverImage
          ) {
            await DrawerService.deleteFile(oldHoverImage);
          }

          const finalVis = dataSnapshot.visibility || 'Public';

          const savePayload = {
            ...markerSnapshot,
            ...dataSnapshot,
            markerGroupID: groupID,
            hoverImage: finalHoverImage,
            visibility: finalVis,
            lastModified: new Date().toISOString(),
          };

          delete savePayload.publicFromDate;
          delete savePayload.isPrivate;

          delete savePayload.file;
          delete savePayload.photos;
          delete savePayload.documents;
          delete savePayload.isNew;

          let finalPhotos = markerSnapshot.photos || [];
          let finalDocs = markerSnapshot.documents || [];

          if (photosDirtyRef.current) {
            const { updatedItemsState, finalPhotoData } = await DrawerService.savePhotos(
              savePayload,
              photoItemsRef.current,
              photoDescriptionsRef.current,
            );
            updatePhotoItems(updatedItemsState, false);
            finalPhotos = finalPhotoData;
          }
          if (docsDirtyRef.current) {
            const { updatedItemsState, finalDocData } = await DrawerService.saveDocs(
              savePayload,
              docItemsRef.current,
            );
            updateDocItems(updatedItemsState, false);
            finalDocs = finalDocData;
          }

          // Step 1: Dispatch the SAVE payload
          onAction('SAVE', savePayload);

          const fullyHydratedMarker = {
            ...savePayload,
            photos: finalPhotos,
            documents: finalDocs,
          };

          pristineSnapshotRef.current = JSON.parse(JSON.stringify(fullyHydratedMarker));

          forceClean();
          justSavedRef.current = true;

          if (localActiveActionRef.current === 'ADD_NEW') {
            linkingGroupIdRef.current = null;
            setAction('EDIT');
          }

          if (!options.silent) {
            showAlert('success', 'Saved successfully');
          }

          // THE FIX: Break React 18 state batching by wrapping subsequent actions in a setTimeout.
          // This allows GoogleMap.js to fully receive and process the 'SAVE' action before 'FORCE_SELECT' overrides it.
          setTimeout(() => {
            onAction('FORCE_SELECT', fullyHydratedMarker);

            setTimeout(() => {
              onAction('EDIT');
            }, 150);
          }, 200);
        } catch (error) {
          console.error('Save Error:', error);
          showAlert('error', 'Failed to save. Please try again.');
        } finally {
          setIsSaving(false);
          setTimeout(() => {
            isSavingRef.current = false;
          }, 300);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
        return;
      }
      onAction(actionName);
      setAction(actionName);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeTab,
      executeClose,
      forceClean,
      onAction,
      selectedMarker,
      setAction,
      showAlert,
      pointsPlaced,
      updatePhotoItems,
      updateDocItems,
      defaultIcon,
      defaultIconID,
      setActiveTool,
      drawingStyles,
      activeTool,
      setDrawingStyles,
      checkTypeExistsInGroup,
      markerIcons,
    ],
  );

  useEffect(() => {
    if (closeTrigger > 0 && closeTrigger !== prevCloseTriggerRef.current) {
      prevCloseTriggerRef.current = closeTrigger;

      if (localActiveActionRef.current === 'ADD_NEW' && !pointsPlaced) {
        return;
      }

      const effectivelyNew =
        selectedMarker && selectedMarker.isNew && !justSavedRef.current;
      const isUnsavedNew =
        (localActiveActionRef.current === 'ADD_NEW' &&
          pointsPlaced &&
          !justSavedRef.current) ||
        effectivelyNew;

      const isCurrentlyDirty =
        markerDirtyRef.current ||
        infoDirtyRef.current ||
        photosDirtyRef.current ||
        docsDirtyRef.current ||
        isUnsavedNew;

      if (isCurrentlyDirty) {
        setUnsavedMessage('Do you want to discard those changes?');
        setShowUnsavedAlert(true);
        return;
      }
      if (
        localActiveActionRef.current !== 'ADD_NEW' &&
        localActiveActionRef.current !== 'EDIT'
      ) {
        handleHeaderAction('CLOSE');
      }
    }
  }, [closeTrigger, handleHeaderAction, pointsPlaced, selectedMarker]);

  useEffect(() => {
    tempDataRef.current = tempData;
  }, [tempData]);

  useEffect(() => {
    let isMounted = true;
    const fetchGroupData = async () => {
      if (!selectedMarker || selectedMarker.isNew || !selectedMarker.id) return;

      let fetchedData = null;
      let groupId = selectedMarker.markerGroupID || linkingGroupIdRef.current;

      try {
        if (groupId) {
          const docRef = doc(db, 'markerGroups', groupId);
          const snap = await getDoc(docRef);
          if (snap.exists()) {
            fetchedData = { markerGroupID: snap.id, ...snap.data() };
          }
        } else {
          const typeKey = (selectedMarker.type || '').toLowerCase();
          let queryField = 'markerID';
          if (typeKey === 'polygon') queryField = 'polygonID';
          if (typeKey === 'line' || typeKey === 'polyline') queryField = 'polylineID';

          const q = query(
            collection(db, 'markerGroups'),
            where(queryField, '==', selectedMarker.id),
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            fetchedData = { markerGroupID: snap.docs[0].id, ...snap.docs[0].data() };
          }
        }

        if (isMounted && fetchedData) {
          setTempData((prev) => {
            const next = { ...prev, ...fetchedData };
            tempDataRef.current = next;
            return next;
          });
          hydrateTabsState({ ...selectedMarker, ...fetchedData });
          linkingGroupIdRef.current = fetchedData.markerGroupID;
        }
      } catch (err) {
        console.error('Error fetching markerGroups data:', err);
      }
    };

    if (
      selectedMarker &&
      selectedMarker.id &&
      lastFetchedIdRef.current !== selectedMarker.id
    ) {
      lastFetchedIdRef.current = selectedMarker.id;
      fetchGroupData();
    } else if (!selectedMarker) {
      lastFetchedIdRef.current = null;
    }

    return () => {
      isMounted = false;
    };
  }, [selectedMarker, hydrateTabsState]);

  // MAIN SYNCHRONIZATION LOGIC
  useEffect(() => {
    if (isSavingRef.current || isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (!selectedMarker) {
      if (localActiveActionRef.current !== 'ADD_NEW' && !isTransitioningRef.current) {
        setAction('None');
        setPointsPlaced(false);
        setIsInfoEditing(false);
        forceClean();
        resetTabsState();
        setTempData({});
        linkingGroupIdRef.current = null;
        createdInSessionRef.current = new Set();

        setActiveTab('MARKERS');
        if (setActiveTool) setActiveTool('MARKER');
      }
      return;
    }

    const hasGeom = !!(
      selectedMarker.position ||
      selectedMarker.paths?.length ||
      selectedMarker.path?.length ||
      selectedMarker.type === 'line' ||
      selectedMarker.type === 'polyline' ||
      selectedMarker.type === 'polygon'
    );

    const prev = tempDataRef.current;
    const isSameMarker = prev && prev.id === selectedMarker.id;
    const isKnownDraft = !!draftsCacheRef.current[selectedMarker.id];

    const isFreshDrop = selectedMarker.isNew && !isSameMarker && !isKnownDraft;
    const isSelectionChange = selectedMarker.id && !isSameMarker && !isFreshDrop;

    const typeKey = (selectedMarker.type || '').toLowerCase();
    if (typeKey === 'marker' && selectedMarker.id)
      draftSiblingsRef.current.marker = selectedMarker.id;
    else if (typeKey === 'polygon' && selectedMarker.id)
      draftSiblingsRef.current.polygon = selectedMarker.id;
    else if ((typeKey === 'line' || typeKey === 'polyline') && selectedMarker.id)
      draftSiblingsRef.current.line = selectedMarker.id;

    if (hasGeom && selectedMarker.id) {
      draftsCacheRef.current[selectedMarker.id] = {
        ...(draftsCacheRef.current[selectedMarker.id] || {}),
        ...selectedMarker,
      };
    }

    const prevGroupId = prev?.markerGroupID || linkingGroupIdRef.current || prev?.id;
    const nextGroupId = selectedMarker.markerGroupID || selectedMarker.id;

    const isDraftSibling =
      (draftSiblingsRef.current.marker &&
        selectedMarker.id === draftSiblingsRef.current.marker) ||
      (draftSiblingsRef.current.polygon &&
        selectedMarker.id === draftSiblingsRef.current.polygon) ||
      (draftSiblingsRef.current.line &&
        selectedMarker.id === draftSiblingsRef.current.line);

    const isSameGroup =
      (prevGroupId && nextGroupId && String(prevGroupId) === String(nextGroupId)) ||
      isDraftSibling;

    const setTabForMarkerType = (marker) => {
      const t = (marker.type || '').toLowerCase();
      let targetTab = 'MARKERS';
      let targetTool = 'MARKER';

      if (t === 'polygon') {
        targetTab = 'POLYGON';
        targetTool = 'POLYGON';
      } else if (t === 'line' || t === 'polyline') {
        targetTab = 'POLYLINE';
        targetTool = 'LINE';
      }

      if (setActiveTool) setActiveTool(targetTool);

      if (['MARKERS', 'POLYGON', 'POLYLINE'].includes(cachedActiveTab)) {
        setActiveTab(targetTab);
      }
    };

    // SCENARIO 1: Brand New Map Drop
    if (isFreshDrop && hasGeom) {
      pristineSnapshotRef.current = JSON.parse(JSON.stringify(selectedMarker));
      setPointsPlaced(true);

      markerDirtyRef.current = true;
      setMarkerDirty(true);
      setIsInfoEditing(true);
      syncGroupStylesToUI(selectedMarker);

      setTempData((currentTemp) => {
        const safeCurrentTemp = { ...currentTemp };
        delete safeCurrentTemp.position;
        delete safeCurrentTemp.path;
        delete safeCurrentTemp.paths;
        delete safeCurrentTemp.color;
        delete safeCurrentTemp.weight;
        delete safeCurrentTemp.icon;
        delete safeCurrentTemp.iconID;
        delete safeCurrentTemp.type;
        delete safeCurrentTemp.id;

        delete safeCurrentTemp.isDashed;
        delete safeCurrentTemp.dashColor;
        delete safeCurrentTemp.dashLength;

        const merged = { ...safeCurrentTemp, ...selectedMarker };
        const typeStr = (selectedMarker.type || '').toLowerCase();

        merged.groupHasMarker =
          currentTemp.groupHasMarker ||
          merged.groupHasMarker === true ||
          !!draftSiblingsRef.current.marker;
        merged.groupHasPolygon =
          currentTemp.groupHasPolygon ||
          merged.groupHasPolygon === true ||
          !!draftSiblingsRef.current.polygon;
        merged.groupHasPolyline =
          currentTemp.groupHasPolyline ||
          merged.groupHasPolyline === true ||
          !!draftSiblingsRef.current.line;

        merged.markerID =
          currentTemp.markerID ||
          merged.markerID ||
          draftSiblingsRef.current.marker ||
          null;
        merged.polygonID =
          currentTemp.polygonID ||
          merged.polygonID ||
          draftSiblingsRef.current.polygon ||
          null;
        merged.polylineID =
          currentTemp.polylineID ||
          merged.polylineID ||
          draftSiblingsRef.current.line ||
          null;

        if (typeStr === 'marker') {
          merged.groupHasMarker = true;
          merged.markerID = selectedMarker.id;
        }
        if (typeStr === 'polygon') {
          merged.groupHasPolygon = true;
          merged.polygonID = selectedMarker.id;
        }
        if (typeStr === 'line' || typeStr === 'polyline') {
          merged.groupHasPolyline = true;
          merged.polylineID = selectedMarker.id;
        }

        merged.markerGroupID =
          linkingGroupIdRef.current ||
          currentTemp.markerGroupID ||
          selectedMarker.markerGroupID;

        tempDataRef.current = merged; // Instant sync
        return merged;
      });
      return;
    }

    // SCENARIO 2: Selection Changed
    if (isSelectionChange) {
      createdInSessionRef.current = new Set();
      pristineSnapshotRef.current = JSON.parse(JSON.stringify(selectedMarker));

      const isCurrentlyDirty =
        markerDirtyRef.current ||
        infoDirtyRef.current ||
        photosDirtyRef.current ||
        docsDirtyRef.current;

      if (isSameGroup && isCurrentlyDirty) {
        setPointsPlaced(true);
        setIsInfoEditing(true);
        setTempData((currentTemp) => {
          const safeCurrentTemp = { ...currentTemp };
          delete safeCurrentTemp.position;
          delete safeCurrentTemp.path;
          delete safeCurrentTemp.paths;
          delete safeCurrentTemp.color;
          delete safeCurrentTemp.weight;
          delete safeCurrentTemp.icon;
          delete safeCurrentTemp.iconID;
          delete safeCurrentTemp.type;
          delete safeCurrentTemp.id;

          delete safeCurrentTemp.isDashed;
          delete safeCurrentTemp.dashColor;
          delete safeCurrentTemp.dashLength;

          const merged = {
            ...safeCurrentTemp,
            ...selectedMarker,
            markerGroupID: nextGroupId,
          };
          const typeStr = (selectedMarker.type || '').toLowerCase();

          merged.groupHasMarker =
            currentTemp.groupHasMarker ||
            merged.groupHasMarker === true ||
            !!draftSiblingsRef.current.marker;
          merged.groupHasPolygon =
            currentTemp.groupHasPolygon ||
            merged.groupHasPolygon === true ||
            !!draftSiblingsRef.current.polygon;
          merged.groupHasPolyline =
            currentTemp.groupHasPolyline ||
            merged.groupHasPolyline === true ||
            !!draftSiblingsRef.current.line;

          merged.markerID =
            currentTemp.markerID ||
            merged.markerID ||
            draftSiblingsRef.current.marker ||
            null;
          merged.polygonID =
            currentTemp.polygonID ||
            merged.polygonID ||
            draftSiblingsRef.current.polygon ||
            null;
          merged.polylineID =
            currentTemp.polylineID ||
            merged.polylineID ||
            draftSiblingsRef.current.line ||
            null;

          if (typeStr === 'marker') {
            merged.groupHasMarker = true;
            merged.markerID = selectedMarker.id;
          }
          if (typeStr === 'polygon') {
            merged.groupHasPolygon = true;
            merged.polygonID = selectedMarker.id;
          }
          if (typeStr === 'line' || typeStr === 'polyline') {
            merged.groupHasPolyline = true;
            merged.polylineID = selectedMarker.id;
          }

          tempDataRef.current = merged; // Instant sync
          return merged;
        });
        syncGroupStylesToUI(selectedMarker);
        setTabForMarkerType(selectedMarker);

        if (
          localActiveActionRef.current !== 'EDIT' &&
          localActiveActionRef.current !== 'ADD_NEW'
        ) {
          setAction('EDIT');
        }
      } else {
        forceClean();
        setPointsPlaced(true);
        setIsInfoEditing(true);
        const raw = JSON.parse(JSON.stringify(selectedMarker));
        const typeStr = (raw.type || '').toLowerCase();

        raw.markerID = raw.markerID || selectedMarker.markerID || null;
        raw.polygonID = raw.polygonID || selectedMarker.polygonID || null;
        raw.polylineID = raw.polylineID || selectedMarker.polylineID || null;

        if (typeStr === 'marker') {
          raw.groupHasMarker = true;
          raw.markerID = raw.id;
        }
        if (typeStr === 'polygon') {
          raw.groupHasPolygon = true;
          raw.polygonID = raw.id;
        }
        if (typeStr === 'line' || typeStr === 'polyline') {
          raw.groupHasPolyline = true;
          raw.polylineID = raw.id;
        }

        setTempData((prev) => {
          tempDataRef.current = raw; // Instant sync
          return raw;
        });
        syncGroupStylesToUI(raw);
        hydrateTabsState(raw);
        setTabForMarkerType(raw);

        if (localActiveActionRef.current !== 'EDIT') {
          setAction('EDIT');
        }
      }
      return;
    }

    // SCENARIO 3: Live updates to geometry during dragging
    if (hasGeom && pointsPlaced && isSameMarker) {
      let isGeomChanged = false;
      const updates = {};

      if (selectedMarker.position && prev.position) {
        if (
          selectedMarker.position.lat !== prev.position.lat ||
          selectedMarker.position.lng !== prev.position.lng
        ) {
          isGeomChanged = true;
          updates.position = selectedMarker.position;
        }
      } else if (selectedMarker.paths || selectedMarker.path) {
        const newPaths = selectedMarker.paths || selectedMarker.path;
        const oldPaths = prev.paths || prev.path;
        if (JSON.stringify(newPaths) !== JSON.stringify(oldPaths)) {
          isGeomChanged = true;
          if (selectedMarker.paths) updates.paths = selectedMarker.paths;
          if (selectedMarker.path) updates.path = selectedMarker.path;
        }
      }

      if (isGeomChanged) {
        if (!justSavedRef.current && !markerDirtyRef.current) {
          markerDirtyRef.current = true;
          setMarkerDirty(true);
        }
        setTempData((currentTemp) => {
          const next = { ...currentTemp, ...updates };
          tempDataRef.current = next; // Instant sync
          return next;
        });

        if (draftsCacheRef.current[selectedMarker.id]) {
          draftsCacheRef.current[selectedMarker.id] = {
            ...draftsCacheRef.current[selectedMarker.id],
            ...updates,
          };
        }
      }
    }

    if (selectedMarker && !selectedMarker.isNew && justSavedRef.current) {
      setTimeout(() => {
        justSavedRef.current = false;
      }, 100);
    }
  }, [
    selectedMarker,
    localActiveAction,
    pointsPlaced,
    forceClean,
    setAction,
    setDrawingStyles,
    resetTabsState,
    hydrateTabsState,
    setActiveTool,
    setActiveTab,
    syncGroupStylesToUI,
  ]);

  useEffect(() => {
    if (setIsDrawerEditing) setIsDrawerEditing(isInfoEditing);
  }, [isInfoEditing, setIsDrawerEditing]);

  const handlePhotoUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setTempData((prev) => {
        const next = { ...prev, hoverImage: url, file: file };
        tempDataRef.current = next; // Instant sync
        return next;
      });
      markerDirtyRef.current = true;
      setMarkerDirty(true);
    }
    e.target.value = '';
  }, []);

  const handleClearPhoto = useCallback(() => {
    setTempData((prev) => {
      const next = { ...prev, hoverImage: null, file: null };
      tempDataRef.current = next; // Instant sync
      return next;
    });
    markerDirtyRef.current = true;
    setMarkerDirty(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return {
    activeTab,
    setActiveTab,
    localActiveAction,
    pointsPlaced,
    isSaving,
    tempData,
    isInfoEditing,
    markerDirty,
    infoDirty,
    photosDirty,
    docsDirty,
    photoItems,
    setPhotoItems: updatePhotoItems,
    photoDescriptions,
    setPhotoDescriptions,
    docItems,
    setDocItems: updateDocItems,
    photoSelectedIds,
    setPhotoSelectedIds,
    photoLastSelectedId,
    setPhotoLastSelectedId,
    docSelectedIds,
    setDocSelectedIds,
    photosTrigger,
    photosDeleteTrigger,
    docsTrigger,
    docsDeleteTrigger,
    showDeleteConfirm,
    showUnsavedAlert,
    unsavedMessage,
    alert,
    showAlert,
    isDirtyGlobal,
    fileInputRef,
    handleHeaderAction,
    handleToolClick,
    handleTabSwitch,
    handleStyleChange,
    handleMarkerFieldChange,
    handleInfoTabChange,
    handlePhotoUpload,
    handleClearPhoto,
    handleConfirmClose,
    handleCancelClose,
    handleConfirmDelete,
    handleCancelDelete,
    draftSiblings: draftSiblingsRef.current,
    rteFontType, // AMENDED: Pass Global RTE Default Font
    rteFontSize, // AMENDED: Pass Global RTE Default Font
  };
}
